import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Music, EyeOff, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage, formatApiErrorDetail } from "@/lib/api";

export default function AdminSongs() {
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/songs");
      setSongs(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load songs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this song? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/songs/${id}`);
      toast.success("Song deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete");
    }
  };

  // Move a song up or down in the list. We keep the visible order in the array,
  // then rewrite everyone's sort_order (0, 1, 2, ...) so it stays clean.
  const moveSong = async (idx, dir) => {
    if (reordering) return;
    const target = idx + dir;
    if (target < 0 || target >= songs.length) return;
    setReordering(true);
    const next = [...songs];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSongs(next); // optimistic UI update
    try {
      await Promise.all(
        next.map((s, i) =>
          s.sort_order === i
            ? null
            : api.put(`/admin/songs/${s.id}`, {
                title: s.title,
                description: s.description || "",
                cover_image: s.cover_image || "",
                suno_url: s.suno_url || "",
                audio_url: s.audio_url || "",
                is_featured: s.is_featured,
                sort_order: i,
              }),
        ),
      );
      toast.success("Order updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save order");
      load(); // revert on error
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-songs-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-songs-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-rule pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
              Listen
            </p>
            <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
              Manage your songs
            </h1>
            <p className="text-sm text-ink-soft mt-2 max-w-xl">
              Showcase your Suno-created songs on the public Listen page. Toggle
              the &quot;Featured&quot; flag to rotate which songs visitors hear.
              Use the ↑ ↓ arrows to reorder — the top song also appears as
              &quot;Latest Song&quot; on the home page.
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/songs/new")}
            data-testid="admin-songs-new-btn"
            className="inline-flex items-center gap-2 px-5 py-3 bg-ink text-paper text-xs tracking-[0.2em] uppercase hover:bg-terracotta transition-colors"
          >
            <Plus size={14} /> New song
          </button>
        </div>

        {loading ? (
          <p className="text-ink-soft mt-10" data-testid="admin-songs-loading">Loading…</p>
        ) : songs.length === 0 ? (
          <div
            data-testid="admin-songs-empty"
            className="mt-10 border border-dashed border-rule bg-paper-2/40 p-12 text-center"
          >
            <Music size={28} className="mx-auto text-terracotta mb-4" />
            <p className="font-serif-display text-2xl italic text-ink-soft mb-3">
              No songs added yet.
            </p>
            <p className="text-sm text-ink-soft mb-6">
              Click &quot;New song&quot; above to add your first Suno track.
            </p>
          </div>
        ) : (
          <ul className="mt-10 divide-y divide-rule" data-testid="admin-songs-list">
            {songs.map((s, i) => (
              <li
                key={s.id}
                data-testid={`admin-song-row-${i}`}
                className="py-5 flex items-center gap-4"
              >
                {s.cover_image ? (
                  <img
                    src={resolveImage(s.cover_image)}
                    alt=""
                    className="w-16 h-16 object-cover border border-rule shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 border border-rule bg-paper-2/40 flex items-center justify-center shrink-0">
                    <Music size={20} className="text-ink-soft" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <p className="font-serif-display text-xl text-ink truncate">
                    {s.title}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    {s.audio_url && (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">
                        MP3 ready
                      </span>
                    )}
                    {s.suno_url && (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">
                        Suno link
                      </span>
                    )}
                    {!s.is_featured && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-ink-soft">
                        <EyeOff size={11} /> Hidden
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => moveSong(i, -1)}
                  disabled={i === 0 || reordering}
                  data-testid={`admin-song-move-up-${i}`}
                  className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={() => moveSong(i, 1)}
                  disabled={i === songs.length - 1 || reordering}
                  data-testid={`admin-song-move-down-${i}`}
                  className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  onClick={() => navigate(`/admin/songs/${s.id}`)}
                  data-testid={`admin-song-edit-${i}`}
                  className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink"
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => remove(s.id)}
                  data-testid={`admin-song-delete-${i}`}
                  className="p-2 border border-rule text-ink-soft hover:text-terracotta hover:border-terracotta"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
